import { isArray } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectSchema, Shape } from 'yup';

import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import { OrgUnitEmployeeRole, ResponsibleEmps } from '~/data/orgunits';
import {
  AlternateManagerRow,
  OrgUnitFormData,
} from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/types';

export const useValidationSchema = (): ObjectSchema<
  Shape<object | undefined, OrgUnitFormData>
> => {
  const { t } = useTranslation();

  const yupStringRequired = yup.string().required(t('fieldIsRequired'));

  const yupOptionIdRequired = yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required(t('fieldIsRequired'))
    .test(
      'required-option',
      t('fieldIsRequired'),
      (value, _context) => !!value && !isNaN(value),
    );

  const yupNumberArray = yup
    .array()
    .of(
      yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required()
        .defined(),
    )
    .min(1, 'min 1')
    .required('req')
    .defined();

  // Tester for yupNumberArray schema
  // TODO Remove when no longer needed
  // Promise.all(
  //   ['string', undefined, [], ['string'], [1], [1, 3]].map((v) =>
  //     yupNumberArray.validate(v).catch((err) => err),
  //   ),
  // )
  //   .then((results) => {
  //     console.group('yupTrees');
  //     results.map((r) => console.log(r));
  //     console.groupEnd();
  //   })
  //   .catch((err) => {
  //     console.log(err, 'err');
  //   });

  // ResponsibleEmps
  const yupResponsibleEmps = yup.object().shape<ResponsibleEmps>({
    fId: yup.string(),
    fEmployeeId: yup.number().nullable().defined(),
    fUnitRole: yup.string().oneOf(Object.values(OrgUnitEmployeeRole)).defined(),
    fStartDate: yup.string(),
    fEndDate: yup.string(),
  });

  const yupResponsibleEmpsArray = yup
    .array<ResponsibleEmps>()
    .of(yupResponsibleEmps.required())
    .required() as yup.ArraySchema<ResponsibleEmps>;

  // Substitute fields
  const yupSubstituteDateString = yup
    .string()
    .defined()
    .when('substituteManager', (substituteManager, schema) => {
      return !!substituteManager
        ? schema.required(t('fieldIsRequired'))
        : schema;
    });

  // AlternateManagerRow
  const yupAlternateManager = yup
    .object()
    .shape<AlternateManagerRow>({
      id: yup.string().required(),
      manager: yup
        .object()
        .nullable()
        .required() as yup.Schema<EmployeeItemInfoData>,
      startDate: yup
        .string()
        .defined()
        .nullable()
        .test(
          'minStartDate',
          'The start date must be at least one day from today',
          function (value) {
            if (value) {
              const { id } = this.parent as AlternateManagerRow;
              if (!id) {
                // Only validate for new entries
                return moment(value).isSameOrAfter(
                  moment().add(1, 'day').startOf('day'),
                );
              }
            }
            return true;
          },
        ),
      endDate: yup
        .string()
        .nullable()
        .test('minEndDate', 'End date must be in the future', function (value) {
          const { startDate } = this.parent as AlternateManagerRow;
          if (startDate && value) {
            return moment(value).isSameOrAfter(moment().startOf('day'));
          }
          return true; // If startDate is not set, skip this validation
        })
        .test(
          'endDateAfterStartDate',
          'The end date must not be before the start date',
          function (value) {
            const { startDate } = this.parent as AlternateManagerRow;
            if (startDate && value) {
              return moment(value).isSameOrAfter(moment(startDate));
            }
            return true; // If startDate is not set, skip this validation
          },
        ) as yup.Schema<string | null>,
      savedWithoutDates: yup.boolean().required(),
    })
    .test(
      'dates-validation',
      'Both start date and end date must be either present or absent',
      function (value) {
        const { startDate, endDate } = value as AlternateManagerRow;
        if ((startDate && !endDate) || (!startDate && endDate)) {
          return this.createError({
            path: 'endDate',
            message:
              'Both start date and end date must be either present or absent',
          });
        }
        return true;
      },
    );

  const yupTestRequiredArray = (
    arrayPath: keyof OrgUnitFormData,
    message: string,
  ) => {
    // Hacky workaround to test if required item array is empty
    // Apply on parent object and provide path to array

    return (
      value: yup.Shape<object | undefined, OrgUnitFormData> | null,
      context: yup.TestContext<object>,
    ) => {
      const path = !!context.path ? `${context.path}.${arrayPath}` : arrayPath;

      const array = !!value && value[arrayPath];

      return !isArray(array) || (isArray(array) && array.length === 0)
        ? context.createError({
            message,
            path: path,
          })
        : true;
    };
  };

  const validationSchema = yup
    .object()
    .shape<OrgUnitFormData>({
      fOrgUnitName: yupStringRequired,
      fOrgUnitNumber: yupStringRequired,
      fOrgUnitType: yupStringRequired,
      fOrgUnitLoc: yupOptionIdRequired,
      fOrgUnitUrl: yup.string(),
      fOrgUnitCreated: yupStringRequired,
      fOrgUnitAvail: yupStringRequired,
      fOrgUnitManager: yupOptionIdRequired,
      fOrgUnitResponsibleEmps: yupResponsibleEmpsArray,
      fOrgUnitContainingTrees: yupNumberArray,
      fOrgUnitCostCenters: yupNumberArray,
      substituteManager: yupResponsibleEmps.nullable().defined(),
      substituteStartDate: yupSubstituteDateString,
      substituteEndDate: yupSubstituteDateString
        .test(
          'minDate',
          'End date must be today or in the future',
          (value, context) => {
            const { substituteManager } = context.parent;
            if (!substituteManager) {
              return true; // No substitute manager assigned, validation passes
            }
            return (
              value === null ||
              moment(value).isSameOrAfter(moment().startOf('day'))
            );
          },
        )
        .test(
          'endDateAfterStartDate',
          'End date cannot be before start date',
          function (value) {
            const { startDate } = this.parent as AlternateManagerRow;
            if (startDate && value) {
              return moment(value).isSameOrAfter(moment(startDate));
            }
            return true;
          },
        ) as yup.Schema<string>,
      alternateManagers: yup
        .array()
        .of(yupAlternateManager.required())
        .required()
        .test(
          'no-overlapping-dates',
          'Overlapping dates are not allowed',
          function (rows) {
            if (!Array.isArray(rows)) return true;
            for (let i = 0; i < rows.length; i++) {
              for (let j = i + 1; j < rows.length; j++) {
                const rowI = rows[i] as Partial<AlternateManagerRow>;
                const rowJ = rows[j] as Partial<AlternateManagerRow>;

                if (
                  rowI.manager?.employeeId === rowJ.manager?.employeeId &&
                  ((moment(rowI.startDate).isSameOrBefore(
                    moment(rowJ.endDate),
                  ) &&
                    moment(rowI.endDate).isSameOrAfter(
                      moment(rowJ.startDate),
                    )) ||
                    (moment(rowJ.startDate).isSameOrBefore(
                      moment(rowI.endDate),
                    ) &&
                      moment(rowJ.endDate).isSameOrAfter(
                        moment(rowI.startDate),
                      )))
                ) {
                  return this.createError({
                    path: `alternateManagers[${i}]`,
                    message: `${rowI.manager?.employee?.fEmpFirstName} ${rowI.manager?.employee?.fEmpLastName} has overlapping dates.`,
                  });
                }
              }
            }
            return true;
          },
        ) as yup.Schema<AlternateManagerRow[]>,
      responsibleHRs: yupResponsibleEmpsArray,
      deputyManagers: yupResponsibleEmpsArray,
    })
    .test(
      'fOrgUnitContainingTrees',
      'trees-required',
      yupTestRequiredArray('fOrgUnitContainingTrees', t('fieldIsRequired')),
    );

  // Tester for complete schema
  // TODO Remove when no longer needed
  // Promise.all([
  //   validationSchema
  //     .validate(
  //       {
  //         fOrgUnitName: 'x',
  //         fOrgUnitNumber: 'x',
  //         fOrgUnitType: 'x',
  //         fOrgUnitLoc: 1,
  //         fOrgUnitUrl: '',
  //         fOrgUnitCreated: '2020-10-10',
  //         fOrgUnitAvail: '2020-10-10',
  //         fOrgUnitManager: 1,
  //         fOrgUnitResponsibleEmps: [],
  //         fOrgUnitContainingTrees: [],
  //         fOrgUnitCostCenters: [],
  //         substituteManager: null,
  //         substituteStartDate: '',
  //         substituteEndDate: '',
  //         alternateManagers: [],
  //         responsibleHRs: [],
  //         deputyManagers: [],
  //       },
  //       { abortEarly: false },
  //     )
  //     .catch((err) => err),
  // ]).then((res) => res.map((r) => console.log(r)));

  return validationSchema;
};
